<script setup>
import { ArrowLeft, ArrowRight, X } from 'lucide-vue-next';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { convertUrlToResizer } from '@/utils/utils';

const props = defineProps({
  images: {
    type: Array,
    required: true,
  },
  startIndex: {
    type: Number,
    default: 0,
  },
  visible: {
    type: Boolean,
    default: false,
  },
  hasWidget: {
    type: Boolean,
    default: false,
  },
});
const emit = defineEmits(['update:visible', 'close', 'checkAvailability']);
const { t } = useI18n();

const currentIndex = ref(props.startIndex);

const currentImage = computed(() => {
  if (props.images.length === 0 || currentIndex.value < 0 || currentIndex.value >= props.images.length) {
    return null;
  }
  return props.images[currentIndex.value];
});

const isFirstImage = computed(() => currentIndex.value === 0);
const isLastImage = computed(() => currentIndex.value === props.images.length - 1);

watch(() => props.visible, (newVal) => {
  if (newVal) {
    currentIndex.value = props.startIndex;
    document.addEventListener('keydown', handleKeydown);
  }
  else {
    document.removeEventListener('keydown', handleKeydown);
  }
});

watch(() => props.startIndex, (newIndex) => {
  if (props.visible) {
    currentIndex.value = newIndex;
  }
});

function close() {
  emit('update:visible', false);
  emit('close');
}

function prevImage() {
  if (!isFirstImage.value) {
    currentIndex.value--;
  }
}

function nextImage() {
  if (!isLastImage.value) {
    currentIndex.value++;
  }
}

function handleKeydown(event) {
  if (!props.visible)
    return;
  switch (event.key) {
    case 'Escape':
      close();
      break;
    case 'ArrowLeft':
      prevImage();
      break;
    case 'ArrowRight':
      nextImage();
      break;
  }
}

onMounted(() => {
  if (props.visible) {
    document.addEventListener('keydown', handleKeydown);
  }
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown);
});
</script>

<template>
  <Transition name="lightbox-fade">
    <div v-if="visible" class="lightbox-overlay" @click.self="close()">
      <button class="lightbox-close" aria-label="Close lightbox" @click="close">
        <X color="white" :size="24" />
      </button>

      <div v-if="images.length > 0" class="lightbox-counter">
        {{ currentIndex + 1 }} / {{ images.length }}
      </div>

      <div class="lightbox-content" @click.self="close()">
        <button
          v-if="!isFirstImage"
          class="lightbox-nav prev"
          aria-label="Previous image"
          @click.stop="prevImage"
        >
          <ArrowLeft color="white" :size="20" />
        </button>
        <img
          v-if="currentImage"
          :src="convertUrlToResizer(currentImage.url)"
          :alt="currentImage.credit || 'Fullscreen image'"
          class="lightbox-image"
        >
        <div v-else class="lightbox-loading">
          Loading...
        </div>
        <button
          v-if="!isLastImage"
          class="lightbox-nav next"
          aria-label="Next image"
          @click.stop="nextImage"
        >
          <ArrowRight color="white" :size="20" />
        </button>
        <button v-if="props.hasWidget" type="button" class="availabilities" @click="emit('checkAvailability')">
          {{ t('images.availabilities') }}
        </button>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.lightbox-fade-enter-active,
.lightbox-fade-leave-active {
  transition: opacity 0.3s ease;
}
.lightbox-fade-enter-from,
.lightbox-fade-leave-to {
  opacity: 0;
}

.lightbox-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 10px; /* Add some padding for smaller screens */
  box-sizing: border-box;
}

.lightbox-close {
  position: absolute;
  top: 25px;
  right: 25px;
  background: rgba(0, 0, 0, 0.3);
  border: none;
  color: white;
  font-size: 3rem;
  cursor: pointer;
  padding: 1rem;
  border-radius: 50%;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001;
  transition: background-color 0.2s ease;
  user-select: none;
  line-height: 1;
}

.lightbox-counter {
  position: absolute;
  top: 25px;
  left: 50%;
  transform: translateX(-50%);
  color: white;
  font-size: 1rem;
  background-color: rgba(0, 0, 0, 0.5);
  padding: 5px 10px;
  border-radius: 4px;
  z-index: 1002;
}

.lightbox-content {
  display: flex;
  align-items: center;
  justify-content: space-between; /* Pushes nav buttons to sides */
  width: 100%;
  height: 100%;
  position: relative;
}

.lightbox-image {
  display: block;
  width: 50vw; /* Ensure image doesn't exceed viewport width */
  max-height: 85vh; /* Ensure image doesn't exceed viewport height, leaving space for controls */
  object-fit: contain;
  margin: 0 auto; /* Center image if nav buttons are not taking space */
  user-select: none;
}

.lightbox-loading {
  color: white;
  font-size: 1.5rem;
  text-align: center;
  width: 100%;
}

.lightbox-nav {
  position: absolute;
  top: 45%;
  background: rgba(0, 0, 0, 0.3);
  border: none;
  color: white;
  font-size: 3rem;
  cursor: pointer;
  padding: 1rem;
  border-radius: 50%;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001;
  transition: background-color 0.2s ease;
  user-select: none;
}

.lightbox-nav:hover {
  background: rgba(0, 0, 0, 0.6);
}

.lightbox-nav.prev {
  left: 1.5rem;
}

.lightbox-nav.next {
  right: 1.5rem;
}

.availabilities {
  position: absolute;
  bottom: 25px;
  left: 50%;
  transform: translateX(-50%);
  appearance: none;
  color: white;
  font-size: 1rem;
  font-weight: var(--yb-text-bold);
  background-color: rgba(0, 0, 0, 0.5);
  padding: 10px 16px;
  border-radius: 4px;
  border: 1px solid var(--yb-color-white);
  z-index: 1002;
  cursor: pointer;
}

@media (max-width: 768px) {
  .lightbox-close {
    top: 10px;
    right: 10px;
    font-size: 2rem;
  }
  .lightbox-counter {
    top: 15px;
    font-size: 0.9rem;
  }
  .lightbox-nav {
    font-size: 2rem;
    width: 45px;
    height: 45px;
    padding: 0.5rem;
  }
  .lightbox-image {
    width: 85vw;
    max-height: 75vh;
  }
  .lightbox-nav.prev {
    margin-right: 5px;
  }
  .lightbox-nav.next {
    margin-left: 5px;
  }
}
</style>
